import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { StatBar } from "@/components/StatBar";
import { conditionFor, STATE_ICON_PATH } from "@/utils/stats";
import { useRun } from "@/state/run";

export function ResourceGauges() {
    const run = useRun(s => s.run);
    const previousGold = useRef<number | null>(null);
    const [goldPulseTick, setGoldPulseTick] = useState(0);

    // 🚨 2026-04-23 Hook Rules 수정: early return 이 hook 뒤에 와야 매 render 마다
    //   hook 호출 순서가 일정. PortraitAnchor 와 동일 버그 유형.
    useEffect(() => {
        if (!run) return;
        if (previousGold.current !== null && previousGold.current !== (run.gold ?? 0)) {
            setGoldPulseTick(tick => tick + 1);
        }
        previousGold.current = run.gold ?? 0;
    }, [run?.gold, run]);

    if (!run) return null;

    const condition = conditionFor(run.stamina, run.mana);

    return (
        <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: tokens.easing.decelerate }}
            className="flex flex-col gap-s rounded-m p-m"
            style={{
                width: 400,
                background: `linear-gradient(180deg, ${tokens.color.ink}C4 0%, ${tokens.color.ink}A8 100%)`,
                border: `1px solid ${tokens.color.brass}33`,
                backdropFilter: "blur(3px)",
                boxShadow: `inset 0 1px 0 ${tokens.color.brass}22`,
            }}
        >
            <div className="flex items-center justify-between">
                <div className="flex flex-col">
                    <span
                        className="font-body"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: 13.5,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            opacity: 0.92,
                            lineHeight: 1,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        원정의 주인
                    </span>
                    <span
                        className="font-display"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                            letterSpacing: "0.04em",
                            textShadow: `0 0 10px ${tokens.color.ember}44`,
                            marginTop: 1,
                        }}
                    >
                        {run.characterName}
                    </span>
                </div>
                <span
                    className="flex items-center gap-xs font-stat rounded-pill px-s py-[2px]"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: tokens.type.size.small,
                        background: `${tokens.color.brass}14`,
                        border: `1px solid ${tokens.color.brass}44`,
                    }}
                >
                    <IconSlot src={STATE_ICON_PATH.condition} fallback={condition.emoji} size={22} />
                    {condition.labelKo}
                </span>
            </div>
            <StatBarWithIcon
                iconSrc={STATE_ICON_PATH.vitality}
                fallback="체"
                label="체력"
                value={run.stamina}
                max={100}
                color={tokens.color.ember}
            />
            <StatBarWithIcon
                iconSrc={STATE_ICON_PATH.spirit}
                fallback="정"
                label="정신"
                value={run.mana}
                max={100}
                color={tokens.color.steel}
            />
            {/* 2026-04-23: 자금 row — brass hair-line 으로 분리 + 숫자 pulse 시 brass glow 강화. */}
            <div
                className="flex items-center justify-between pt-s"
                style={{
                    borderTop: `1px solid ${tokens.color.brass}22`,
                    background: `linear-gradient(180deg, ${tokens.color.brass}0A 0%, transparent 80%)`,
                    marginTop: 2,
                    marginLeft: -4,
                    marginRight: -4,
                    paddingLeft: 4,
                    paddingRight: 4,
                    borderRadius: 4,
                }}
            >
                <span className="flex items-center gap-s" style={{ fontSize: tokens.type.size.small }}>
                    <span aria-hidden style={{ fontSize: 18, opacity: 0.9, color: tokens.color.brass, textShadow: `0 0 8px ${tokens.color.brass}88` }}>¤</span>
                    <span
                        className="font-body"
                        style={{
                            color: tokens.color.parchment,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            fontSize: 14.5,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        자금 · 금화
                    </span>
                </span>
                <motion.span
                    key={`gold-${goldPulseTick}`}
                    className="font-stat"
                    initial={{ scale: 1, opacity: 0.92 }}
                    animate={{
                        scale: [1, 1.16, 1],
                        opacity: [0.92, 1, 0.92],
                        textShadow: [
                            `0 0 0px ${tokens.color.brass}00`,
                            `0 0 14px ${tokens.color.brass}AA`,
                            `0 0 0px ${tokens.color.brass}00`,
                        ],
                    }}
                    transition={{ duration: 0.7, ease: tokens.easing.decelerate }}
                    style={{ color: tokens.color.brass, fontSize: tokens.type.size.body, letterSpacing: "0.02em", fontWeight: 600 }}
                >
                    {(run.gold ?? 0).toLocaleString()}
                    <span style={{ opacity: 0.7, fontSize: tokens.type.size.small, marginLeft: 2 }}>g</span>
                </motion.span>
            </div>
        </motion.div>
    );
}

function StatBarWithIcon({
    iconSrc, fallback, ...rest
}: {
    iconSrc: string;
    fallback: string;
    label: string;
    value: number;
    max: number;
    color: string;
}) {
    return (
        <div className="flex items-center gap-s">
            <IconSlot src={iconSrc} fallback={fallback} size={34} />
            <div className="flex-1">
                <StatBar {...rest} />
            </div>
        </div>
    );
}

function IconSlot({ src, fallback, size }: { src: string; fallback: string; size: number }) {
    return (
        <div
            className="relative flex items-center justify-center flex-shrink-0"
            style={{ width: size, height: size }}
        >
            <img
                src={src}
                alt=""
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                className="absolute inset-0 w-full h-full object-contain"
                style={{ opacity: 0.9 }}
            />
            <span
                className="relative select-none"
                style={{ fontSize: size * 0.45, opacity: 0.9 }}
                aria-hidden
            >
                {fallback}
            </span>
        </div>
    );
}
